import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import InputField from '../../../../Components/Form/components/InputField';
import DateField from '../../../../Components/Form/components/DateField';
import HealthInsuranceCompanySelectField from '../../../Base/Components/Inputs/HealthInsuranceCompanySelect/HealthInsuranceCompanySelectField';
import GenderSelectField from '../../../Base/Components/Inputs/GenderSelect/GenderSelectField';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import moment, { Moment } from 'moment';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { Meta, usePatientProfileDataQuery } from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TimePicker from '../../../../Components/Base/TimePicker/TimePicker';
import FormControl from '../../../../Components/Form/FormControl';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import FormContainer from '../../../../Components/Form/FormContainer';
import NumberField from '../../../../Components/Form/components/NumberField';
import { useValidationMessage } from '../../../Base/Hooks/useValidationMessage';
import DoctorSelectField from '../../../../Components/Form/components/DoctorSelectField';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import PhoneNumberField from '../../../../Components/PhoneNumber/PhoneNumberField';
import TextAreaField from '../../../../Components/Form/components/TextAreaField';
import useMeta from '../../../Meta/Hooks/useMeta';
import { MetaKey } from '../../../Meta/Enums/MetaKey.enum';
import { DocumentContentSubType } from '../../../Documents/Enums/documentContentSubType.enum';
import DiagnosisSelectSpecialField from '../../../../Components/Base/DiagnosisSelectSpecial/DiagnosisSelectSpecialField';
import LaboratoryPrimarySampleSelectionField from '../LaboratoryPrimarySampleSelection/LaboratoryPrimarySampleSelectionField';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { validatePhoneNumber } from '../../../Base/Helpers/phoneNumber.helper';

interface Props {
	onSuccess: (values: LaboratoryRequestCreateRequestHeaderFormValues) => void;
	patientId: string;
	initialValues?: LaboratoryRequestCreateRequestHeaderFormValues;
	laboratoryCode: string;
	requisitionId: string;
}

export interface LaboratoryRequestCreatePatient {
	firstName: string;
	lastName: string;
	insuranceNumber: string;
	birthDate: Moment;
	address: {
		street: string;
		descriptiveNumber: string;
		guideNumber?: string;
		city: string;
		postCode: string;
	};
	insuranceCompany: SelectValue;
	gender: SelectValue;
	diagnosis: SelectValue;
	contactPhoneNumber?: string;
	contactEmail?: string;
	height?: string;
	weight?: string;
}

export interface LaboratoryRequestCreateDocumentInfo {
	tookBy?: SelectValue;
	tookByCustom?: string;
	date?: Moment;
	time?: Moment;
	primarySampleType?: string;
	statim: boolean;
	selfPaid: boolean;
	statimPhoneNumber?: string;
	note?: string;
}

export interface LaboratoryRequestCreateRequestHeaderFormValues {
	patient: LaboratoryRequestCreatePatient;
	documentInfo: LaboratoryRequestCreateDocumentInfo;
}

export const PRIMARY_SAMPLE_TYPE = [
	{
		title:
			'component.laboratoryRequestCreateRequestHeaderForm.primarySampleTypeOption.blood',
		value: 'blood',
	},
	{
		title:
			'component.laboratoryRequestCreateRequestHeaderForm.primarySampleTypeOption.urine',
		value: 'urine',
	},
	{
		title:
			'component.laboratoryRequestCreateRequestHeaderForm.primarySampleTypeOption.spit',
		value: 'spit',
	},
	{
		title:
			'component.laboratoryRequestCreateRequestHeaderForm.primarySampleTypeOption.stool',
		value: 'stool',
	},
];

const LaboratoryRequestCreateRequestHeaderForm: React.FC<Props> = ({
	onSuccess,
	patientId,
	initialValues,
	laboratoryCode,
	requisitionId,
}) => {
	const isUnilabs = laboratoryCode === 'unilabs';
	const isSynlab = laboratoryCode === 'synlab';
	const [isWorkerOutsideOfMEDDI, setIsWorkerOutsideMEDDI] = useState(false);
	const { getMessageByKey } = useValidationMessage(
		'validation.laboratoryRequestCreateRequestHeaderForm',
	);

	const validationSchema = yup.object({
		patient: yup.object({
			firstName: yup.string().required(getMessageByKey('firstName')),
			lastName: yup.string().required(getMessageByKey('lastName')),
			insuranceNumber: yup
				.string()
				.required(getMessageByKey('insuranceNumber'))
				.typeError(getMessageByKey('insuranceNumber')),
			birthDate: yup
				.date()
				.required(getMessageByKey('birthDate'))
				.typeError(getMessageByKey('birthDate')),
			address: yup.object({
				street: yup.string().required(getMessageByKey('street')),
				descriptiveNumber: yup
					.string()
					.required(getMessageByKey('descriptiveNumber')),
				guideNumber: yup.string().notRequired(),
				city: yup.string().required(getMessageByKey('city')),
				postCode: yup.string().required(getMessageByKey('postCode')),
			}),
			insuranceCompany: isUnilabs
				? yup.object().nullable().notRequired()
				: yup.object({
						label: yup.string(),
						value: yup.string().required(getMessageByKey('insuranceCompany')),
				  }),
			gender: yup
				.object({
					label: yup.string(),
					value: yup.string().required(getMessageByKey('gender')),
				})
				.typeError(getMessageByKey('gender')),
			diagnosis: yup
				.object({
					label: yup.string().nullable(),
					value: yup.string().nullable(),
				})
				.nullable()
				.test('required', getMessageByKey('diagnosis'), v => !!v?.value),
			contactEmail: yup.string().nullable(),
			contactPhoneNumber: yup
				.string()
				.nullable()
				.test('phone', getMessageByKey('contactPhoneNumber'), value => {
					if (!value) {
						return true;
					}
					return isValidPhoneNumber(value);
				}),
			height: yup.string().nullable(),
			weight: yup.string().nullable(),
			note: yup.string().nullable(),
		}),
		documentInfo: yup.object({
			primarySampleType: isSynlab
				? yup.mixed()
				: yup.mixed().nullable().required(getMessageByKey('primarySampleType')),
			selfPaid: yup.boolean().nullable(),
		}),
	});

	const { data, isLoading, refetch } = usePatientProfileDataQuery({
		userId: patientId,
	});
	const { getMetaAsObject } = useMeta();
	const formMethods = useForm({ resolver: yupResolver(validationSchema) });
	const handleSubmit = (values: any) => {
		onSuccess(values as LaboratoryRequestCreateRequestHeaderFormValues);
	};

	useEffect(() => {
		refetch();
	}, []);

	useEffect(() => {
		if (initialValues?.patient && initialValues?.documentInfo) {
			formMethods.reset(initialValues);
			return;
		}

		const user = data?.user;
		if (!user) return;
		const userProfile = user.profile;
		const userMeta = getMetaAsObject(user.meta as Meta[]);
		if (!userProfile || !userMeta) return;

		const diagnoses = userMeta[MetaKey.DIAGNOSIS];
		const diagnosis = diagnoses?.find(
			(item: { label: string; value: string; subType: string }) =>
				item.subType === DocumentContentSubType.MAIN_DIAGNOSIS,
		);

		formMethods.reset({
			patient: {
				firstName: userProfile.firstName,
				lastName: userProfile.lastName,
				insuranceNumber: userProfile.insuranceNumber,
				birthDate: userProfile.birthDate,
				address: {
					street: userProfile.address?.street,
					descriptiveNumber: userProfile.address?.descriptiveNumber,
					guideNumber: userProfile.address?.guideNumber,
					city: userProfile.address?.city,
					postCode: userProfile.address?.postCode,
				},
				insuranceCompany: isUnilabs
					? null
					: userProfile.insuranceCompanyTermItem?.id,
				gender: userProfile.genderTermItem?.id,
				contactEmail: user.email,
				contactPhoneNumber: validatePhoneNumber(user.formattedPhoneNumber),
				height: userProfile?.height,
				weight: userProfile?.weight,
				diagnosis,
			},
		});
	}, [data, initialValues, isUnilabs]);

	const STATIMValue = formMethods.watch('documentInfo.statim');

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div>
			<FormWrapper formMethods={formMethods}>
				<FormContainer name='patient'>
					<div className='row'>
						<div className='col-6'>
							<InputField
								label='component.laboratoryRequestCreateRequestHeaderForm.firstName'
								name='firstName'
								isWrapped
								isRequired
							/>
						</div>
						<div className='col-6'>
							<InputField
								label='component.laboratoryRequestCreateRequestHeaderForm.lastName'
								name='lastName'
								isWrapped
								isRequired
							/>
						</div>
					</div>
					<div className='row'>
						<div className='col-6'>
							<InputField
								label='component.laboratoryRequestCreateRequestHeaderForm.insuranceNumber'
								name='insuranceNumber'
								isWrapped
								isRequired
							/>
						</div>
						<div className='col-6'>
							<DateField
								label='component.laboratoryRequestCreateRequestHeaderForm.birthDate'
								name='birthDate'
								isRequired
							/>
						</div>
					</div>
					<FormContainer name='address'>
						<div className='row'>
							<div className='col-4'>
								<InputField
									label='component.laboratoryRequestCreateRequestHeaderForm.street'
									name='street'
									isRequired
								/>
							</div>
							<div className='col-2'>
								<NumberField
									label='component.laboratoryRequestCreateRequestHeaderForm.descriptiveNumber'
									name='descriptiveNumber'
									isRequired
								/>
							</div>
							<div className='col-2'>
								<NumberField
									label='component.laboratoryRequestCreateRequestHeaderForm.guideNumber'
									name='guideNumber'
								/>
							</div>
							<div className='col-2'>
								<InputField
									label='component.laboratoryRequestCreateRequestHeaderForm.city'
									name='city'
									isRequired
								/>
							</div>
							<div className='col-2'>
								<NumberField
									label='component.laboratoryRequestCreateRequestHeaderForm.postCode'
									name='postCode'
									isRequired
								/>
							</div>
						</div>
					</FormContainer>
					<div className='row'>
						<div className='col-3'>
							{!isUnilabs && (
								<HealthInsuranceCompanySelectField
									label='component.laboratoryRequestCreateRequestHeaderForm.insuranceCompany'
									name='insuranceCompany'
									isWrapped
									isRequired
									includeSelfPayer
								/>
							)}
						</div>
						<div className='col-3'>
							<GenderSelectField
								name='gender'
								label='component.laboratoryRequestCreateRequestHeaderForm.gender'
								valueKey='id'
								isWrapped
								isRequired
							/>
						</div>
						<div className='col-3'>
							<NumberField
								name='height'
								label='component.laboratoryRequestCreateRequestHeaderForm.height'
								suffix='cm'
								isWrapped
							/>
						</div>
						<div className='col-3'>
							<NumberField
								name='weight'
								label='component.laboratoryRequestCreateRequestHeaderForm.weight'
								suffix='kg'
								isWrapped
							/>
						</div>
					</div>
					<div className='row'>
						<div className='col-3'>
							<InputField
								name='contactEmail'
								label='component.laboratoryRequestCreateRequestHeaderForm.contactEmail'
								isWrapped
							/>
						</div>
						<div className='col-3'>
							<PhoneNumberField
								name='contactPhoneNumber'
								label='component.laboratoryRequestCreateRequestHeaderForm.contactPhoneNumber'
								isWrapped
							/>
						</div>
						<div className='col-6'>
							<DiagnosisSelectSpecialField
								name='diagnosis'
								label='component.laboratoryRequestCreateRequestHeaderForm.diagnosis'
								isRequired
							/>
						</div>
					</div>
				</FormContainer>
				<FormContainer name='documentInfo'>
					<div className='row'>
						<div className='col-6'>
							{isWorkerOutsideOfMEDDI ? (
								<InputField
									label='component.laboratoryRequestCreateRequestHeaderForm.tookBy'
									name='tookByCustom'
								/>
							) : (
								<InputWrapper>
									<DoctorSelectField
										label='component.laboratoryRequestCreateRequestHeaderForm.tookBy'
										name='tookBy'
									/>
									<LinkButton
										title='component.laboratoryRequestCreateRequestHeaderForm.tookByCustomToggle'
										theme={Theme.BLUE}
										onClick={() => setIsWorkerOutsideMEDDI(true)}
									/>
								</InputWrapper>
							)}
						</div>
						<div className='col-3'>
							<DateField
								label='component.laboratoryRequestCreateRequestHeaderForm.date'
								name='date'
								minDate={moment()}
							/>
						</div>
						<div className='col-3'>
							<FormControl
								label='component.laboratoryRequestCreateRequestHeaderForm.time'
								name='time'>
								<TimePicker />
							</FormControl>
						</div>
					</div>
					<div className='row'>
						<div className='col-2 pt-3'>
							<CheckboxField name='statim' tx='STATIM' isRequired isWrapped />
							{STATIMValue && (
								<PhoneNumberField
									name='statimPhoneNumber'
									label='component.laboratoryRequestCreateRequestHeaderForm.statimPhoneNumber'
								/>
							)}
						</div>
						{isUnilabs && (
							<div className='col-2 pt-3'>
								<CheckboxField
									name='selfPaid'
									tx='component.laboratoryRequestCreateRequestHeaderForm.selfPaid'
									isRequired
									isWrapped
								/>
							</div>
						)}
					</div>
					{!isSynlab && (
						<div className='row'>
							<div className='col-12'>
								<LaboratoryPrimarySampleSelectionField
									label='component.laboratoryRequestCreateRequestHeaderForm.primarySampleType'
									name='primarySampleType'
									requisitionId={requisitionId}
									isRequired
								/>
							</div>
						</div>
					)}
					<div className='row'>
						<div className='col-12'>
							<TextAreaField
								name='note'
								label='component.laboratoryRequestCreateRequestHeaderForm.note'
							/>
						</div>
					</div>
				</FormContainer>
				<div className='mt-3'>
					<SubmitButton onSubmit={handleSubmit} title='base.shared.continue' />
				</div>
			</FormWrapper>
		</div>
	);
};

export default LaboratoryRequestCreateRequestHeaderForm;
